import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';

/**
 * Property visuals (design/Objekte, design/Main, design/ObjektAnlegen): card illustration, house frame with roof,
 * floors and ground, unit cards inside the floors, small legends. Colours only from tokens (css/objects.css).
 */
export type BuildingKind = 'house' | 'block' | 'flat';
const ART: Record<BuildingKind, { roof: string; body: [number, number, number, number]; windows: string }> = {
  house: { roof: 'M40 52 L100 14 L160 52 Z', body: [48, 52, 104, 82], windows: 'M64 72 H70 M96 72 H102 M128 72 H134 M64 100 H70 M96 100 H102 M128 100 H134' },
  block: {
    roof: 'M14 30 H186 V34 H14 Z',
    body: [14, 34, 172, 100],
    windows: [52, 80, 108].map((y) => [30, 56, 82, 108, 134, 160].map((x) => `M${x} ${y} H${x + 6}`).join(' ')).join(' '),
  },
  flat: { roof: 'M50 40 L100 10 L150 40 Z', body: [56, 40, 88, 94], windows: 'M72 60 H78 M122 60 H128 M72 84 H78 M122 84 H128 M72 108 H78 M122 108 H128' },
};

/** Line illustration of a building for property cards. Decorative. */
export function BuildingArt({ kind = 'house' }: { kind?: BuildingKind }) {
  const a = ART[kind];
  const [x, y, w, h] = a.body;
  return (
    <svg className="building-art" width="200" height="134" viewBox="0 0 200 134" fill="none" aria-hidden focusable="false">
      <path d={a.roof} className="ba-roof" />
      <rect x={x} y={y} width={w} height={h} className="ba-body" />
      <path d={a.windows} className="ba-windows" />
    </svg>
  );
}

/** House drawing: roof (with chimney), one row per floor (top floor first), ground line; `footer` e.g. a legend. */
/** `ground`: drawn right under the ground line, e.g. the garden strip (Außenflächen). */
export function HouseFrame({ label, children, footer, compact, ground }: { label: string; children: ReactNode; footer?: ReactNode; compact?: boolean; ground?: ReactNode }) {
  return (
    <div className={`house-frame ${compact ? 'compact' : ''}`}>
      <svg className="house-roof" viewBox="0 0 820 110" preserveAspectRatio="none" aria-hidden focusable="false">
        <polygon points="40,108 410,8 780,108" />
        <rect x="560" y="30" width="34" height="50" />
      </svg>
      <div className="house-body" role="list" aria-label={label}>
        {children}
      </div>
      <div className="house-ground" aria-hidden />
      {ground}
      {footer}
    </div>
  );
}

/**
 * One floor row inside HouseFrame: label column left, units right. `below` shades a floor below ground
 * (cellar, basements) and the first such row carries the ground line (css/objects.css).
 */
export function HouseFloor({ label, children, below }: { label: ReactNode; children: ReactNode; below?: boolean }) {
  return (
    <div className={`house-floor${below ? ' is-below' : ''}`} role="listitem">
      <div className="house-floor-label">{label}</div>
      <div className="house-floor-units">{children}</div>
    </div>
  );
}

export type HouseUnitTone = 'ok' | 'warn' | 'neutral';
/**
 * Unit card inside a floor: title + status (dot + text, colour never alone), name line, area and people.
 * `variant` vacant/aux draws the dashed outline of an empty or auxiliary unit.
 */
/** `locked`: short reason of a unit beyond the plan (docs/BILLING.md §5) – the card is greyed with a lock line. */
/** Occupancy tint of a unit (tokens color.unitState, docs/08 "Belegung einer Einheit"); the status text stays next to it. */
export type UnitState = 'rented' | 'soon' | 'vacant' | 'own' | 'off';
/**
 * `state`: occupancy tint of the whole card. `meters`: small, non-interactive meter status line (icons + text).
 * `footer`: an extra line at the bottom, never more than one short line.
 */
export function HouseUnit({ to, title, status, tone = 'ok', state, name, area, people, variant, ariaLabel, locked, meters, footer }: { to?: string; title: ReactNode; status: ReactNode; tone?: HouseUnitTone; state?: UnitState; name?: ReactNode; area?: ReactNode; people?: ReactNode; variant?: 'vacant' | 'aux'; ariaLabel?: string; locked?: ReactNode; meters?: ReactNode; footer?: ReactNode }) {
  const cls = ['house-unit', variant ? `is-${variant}` : '', state ? `st-${state}` : '', locked ? 'is-locked' : ''].filter(Boolean).join(' ');
  const body = (
    <>
      <span className="hu-head">
        <span className="hu-title">{title}</span>
        <span className={`hu-status ${tone}`}><span className="hu-dot" aria-hidden />{status}</span>
      </span>
      {name != null && <span className="hu-name">{name}</span>}
      <span className="hu-foot"><span>{area}</span><span>{people}</span></span>
      {meters && <span className="hu-meters">{meters}</span>}
      {locked && <span className="hu-lock"><Icon name="lock" size={13} strokeWidth={1.8} aria-hidden />{locked}</span>}
      {footer != null && <span className="hu-extra">{footer}</span>}
    </>
  );
  return to ? <Link to={to} className={cls} aria-label={ariaLabel}>{body}</Link> : <div className={cls} aria-label={ariaLabel}>{body}</div>;
}

export type LegendSwatch = 'plot-common' | 'plot-unit1' | 'plot-unit2' | 'plot-unit3' | 'plot-unit4' | 'plot-unit5' | 'plot-unit6' | 'unit-rented' | 'unit-soon' | 'unit-vacant' | 'unit-own' | 'dot-ok' | 'dot-warn' | 'dot-empty' | 'dot-event' | 'bar-brand' | 'bar-ended' | 'bar-planned' | 'bar-dashed' | 'box-covered' | 'box-manual' | 'box-review' | 'box-missing' | 'box-expected';
/** Small legend line (swatch + text) under charts and the house drawing. */
export function Legend({ items, className = '' }: { items: { key: string; swatch: LegendSwatch; label: ReactNode }[]; className?: string }) {
  return (
    <span className={`legend ${className}`}>
      {items.map((i) => (
        <span key={i.key} className="legend-item"><span className={`legend-sw ${i.swatch}`} aria-hidden />{i.label}</span>
      ))}
    </span>
  );
}

/** Year tabs in a pill track (design/Hausakte): newest year first (same order as the mobile app), arrow keys move, one tab stop. */
/** Tabs shown before the rest moves into the "Ältere Jahre" select: the newest year and the three before it. */
export const YEAR_TABS = 4;

/**
 * Year switch: the newest {@link YEAR_TABS} years as tabs (arrow keys move between them), every older year in one
 * "Ältere Jahre ▾" select at the end. A selected older year shows as the active item of that select.
 */
export function YearSwitch({ years: given, value, onChange, label, tabs = YEAR_TABS }: { years: number[]; value: number | undefined; onChange: (y: number) => void; label: string; tabs?: number }) {
  const { t } = useTranslation('common');
  const years = [...new Set(given)].sort((a, b) => b - a);
  const recent = years.slice(0, tabs);
  const older = years.slice(tabs);
  const olderActive = value != null && older.includes(value);
  const move = (dir: 1 | -1, el: HTMLElement) => {
    const i = recent.indexOf(value ?? recent[0]!);
    const next = i < 0 ? recent[dir === 1 ? 0 : recent.length - 1]! : recent[(i + dir + recent.length) % recent.length]!;
    onChange(next);
    requestAnimationFrame(() => el.closest('[role="tablist"]')?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus());
  };
  return (
    <div className="year-switch">
      <div className="year-switch-tabs" role="tablist" aria-label={label} onKeyDown={(e) => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); move(e.key === 'ArrowRight' ? 1 : -1, e.target as HTMLElement); } }}>
        {recent.map((y) => (
          <button key={y} type="button" role="tab" aria-selected={y === value} tabIndex={y === value || (!recent.includes(value!) && y === recent[0]) ? 0 : -1} onClick={() => onChange(y)}>{y}</button>
        ))}
      </div>
      {older.length > 0 && (
        <select className={`year-switch-older${olderActive ? ' active' : ''}`} aria-label={t('ui.yearSwitch.olderLabel', { label })} value={olderActive ? String(value) : ''} onChange={(e) => e.target.value && onChange(Number(e.target.value))}>
          {!olderActive && <option value="">{t('ui.yearSwitch.older')}</option>}
          {older.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      )}
    </div>
  );
}
